import {
  addLandedPenalty,
  assignmentOf,
  getDexieStore,
  getLocalDb,
  liveQuery,
  pruneLandedPenalties,
  type LandedPenalty,
  type OutboxEntry,
  type ServerPenaltyRow,
  type SyncStore,
} from '@flm9/sync';

// Landed penalties (Dexie sync-store meta), same as the native app.
// A penalty add/remove leaves the outbox when it is acked, but the roster the
// score screen is created from (sessions.liveAssignments, held by App.tsx)
// only shows it after a refetch — which fails if the device is offline again.
// Opening Timer-Debug unmounts the score screen and returning recreates it
// from that roster, so an in-memory bridge would lose the change. Every landed
// penalty change is therefore kept here, per assignment, until a roster row
// agrees with it (`pruneLandedForRoster`); 7-day age cap.
const LANDED_META_PREFIX = 'penalties.landed:';
const LANDED_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function parseLanded(raw: string | undefined): LandedPenalty[] {
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as LandedPenalty[]) : [];
  } catch {
    return [];
  }
}

async function readLanded(store: SyncStore, assignmentId: string): Promise<LandedPenalty[]> {
  return parseLanded(await store.getMeta(LANDED_META_PREFIX + assignmentId));
}

/**
 * Remember penalty entries that reached the server (drainer success before
 * the ack, failed-but-landed reconcile). Other ops are ignored.
 */
export async function recordLandedPenalties(entries: readonly OutboxEntry[]): Promise<void> {
  const penalties = entries.filter((e) => e.op === 'penalty.add' || e.op === 'penalty.remove');
  if (penalties.length === 0) return;
  try {
    const store = getDexieStore();
    await store.transaction(async (tx) => {
      const byAssignment = new Map<string, OutboxEntry[]>();
      for (const e of penalties) {
        const id = assignmentOf(e);
        if (id) byAssignment.set(id, [...(byAssignment.get(id) ?? []), e]);
      }
      const now = Date.now();
      for (const [id, list] of byAssignment) {
        let landed = pruneLandedPenalties(await readLanded(tx, id), null, now, LANDED_MAX_AGE_MS);
        for (const e of list) landed = addLandedPenalty(landed, e, now);
        await tx.setMeta(LANDED_META_PREFIX + id, JSON.stringify(landed));
      }
    });
  } catch (err) {
    // Display only: the server has the change either way.
    console.warn('landed penalty not recorded', err);
  }
}

/**
 * Drop landed penalty changes the roster row already shows (`server` = the
 * roster's penalties of this assignment) and those past the age cap. Writes
 * only when something changes.
 */
export async function pruneLandedForRoster(
  assignmentId: string,
  server: readonly ServerPenaltyRow[],
): Promise<void> {
  const store = getDexieStore();
  const before = await readLanded(store, assignmentId);
  if (before.length === 0) return;
  if (pruneLandedPenalties(before, server, Date.now(), LANDED_MAX_AGE_MS).length === before.length)
    return;
  await store.transaction(async (tx) => {
    const kept = pruneLandedPenalties(
      await readLanded(tx, assignmentId),
      server,
      Date.now(),
      LANDED_MAX_AGE_MS,
    );
    await tx.setMeta(LANDED_META_PREFIX + assignmentId, JSON.stringify(kept));
  });
}

/** Live list of this assignment's landed penalty changes. */
export function watchLanded(
  assignmentId: string,
  onChange: (landed: LandedPenalty[]) => void,
): () => void {
  const db = getLocalDb();
  const sub = liveQuery(() => db.meta.get(LANDED_META_PREFIX + assignmentId)).subscribe({
    next: (row) => onChange(parseLanded(row?.value)),
    error: (err) => console.error('landed penalty query failed', err),
  });
  return () => sub.unsubscribe();
}
